/**
 * The admin Overview tab: usage analytics, aggregates only, no third-party
 * visitor tracking. Two data sources, both real:
 *
 *   Phase 1 -- submission analytics from GET /admin/stats (DynamoDB). Always
 *              present once the API is up.
 *   Phase 2 -- site traffic from stats/traffic.json, summarised from real
 *              CloudFront access logs by a scheduled Lambda. Absent until that
 *              infra is brought up; when absent we say so plainly and show no
 *              numbers. Nothing here is ever invented or placeheld.
 *
 * A 401 from the stats route re-gates through onAuthError, exactly like the
 * queue. Charts are hand-rolled inline SVG (see ./charts).
 */

import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { exportSigners, fetchStats, fetchTraffic } from '../lib/adminApi';
import type { StatsResponse, TrafficStats } from '../shared/types';
import { isLogStale, readLogFreshness } from '../shared/logFreshness';
import { StatTile } from './charts/StatTile';
import { ColumnChart } from './charts/ColumnChart';
import { BarChart } from './charts/BarChart';

/** Short UTC day label (e.g. "Jan 30") for a YYYY-MM-DD string. */
function dayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function StatsSection({ stats }: { stats: StatsResponse }) {
  const rate =
    stats.approvalRate === null
      ? 'Not yet'
      : `${Math.round(stats.approvalRate * 100)}%`;

  const perDay = stats.perDay.map((d) => ({ label: dayLabel(d.date), value: d.count }));

  return (
    <section className="overview__section" aria-labelledby="overview-submissions">
      <h2 className="overview__heading" id="overview-submissions">
        Submissions
      </h2>

      <div className="overview__tiles">
        <StatTile label="Total" value={String(stats.total)} />
        <StatTile label="Pending" value={String(stats.byStatus.pending)} />
        <StatTile label="Approved" value={String(stats.byStatus.approved)} />
        <StatTile label="Rejected" value={String(stats.byStatus.rejected)} />
        <StatTile
          label="Approval rate"
          value={rate}
          hint="approved / decided"
        />
      </div>

      <ColumnChart
        data={perDay}
        caption={`Submissions per day (last ${stats.windowDays} days)`}
      />

      {stats.byState.length > 0 && (
        <BarChart
          data={stats.byState.map((s) => ({ label: s.key, value: s.count }))}
          caption="By state"
        />
      )}

      {stats.byWard.length > 0 && (
        <BarChart
          data={stats.byWard.map((w) => ({ label: `Ward ${w.key}`, value: w.count }))}
          caption="By DC ward"
        />
      )}

      {/*
        Optional-chained even though the type says byRef is always present: this
        bundle can be live for a few minutes before the API Lambda that fills the
        field in is redeployed. Absent data hides the chart; it never becomes a
        row of zeros that would read as "this link produced nothing".
      */}
      {(stats.byRef?.length ?? 0) > 0 && (
        <BarChart
          data={stats.byRef.map((r) => ({ label: r.key, value: r.count }))}
          caption="Signups by reference link"
        />
      )}
    </section>
  );
}

/**
 * How to mint a reference link, in the one place the numbers show up. Without
 * this the admin would have to read the validator to learn the slug rules.
 * The origin is copy for a human to read, never a value any logic depends on.
 */
function ReferenceLinkNote() {
  return (
    <p className="overview__caveat">
      Reference links: give a partner org a link like
      https://deletedoordash.com/?ref=their-name and this page counts their page
      views and signups under that tag. A tag can use lowercase letters, numbers,
      and hyphens, up to 40 characters. Visitors never see it, and it never
      appears on the public wall.
    </p>
  );
}

/**
 * Warns when the summary was built from stale (or no) logs, so honest zeros
 * from a broken pipeline are never read as "nobody visited". Renders nothing
 * when the summary predates the freshness fields (unknown, same rule as byRef)
 * or when the logs are fresh. Staleness is the SHARED rule the Lambda uses.
 */
function StaleLogsWarning({ traffic }: { traffic: TrafficStats }) {
  const freshness = readLogFreshness(traffic);
  if (!freshness || !isLogStale(freshness, new Date())) return null;
  const message =
    freshness.newestLogAt === null
      ? 'No traffic logs have arrived. The numbers below are not real zeros.'
      : `Traffic logs have not arrived since ${dayLabel(
          freshness.newestLogAt.slice(0, 10),
        )}. The numbers below are not real zeros.`;
  return (
    <p className="notice notice--error" role="alert">
      {message}
    </p>
  );
}

function TrafficSection({ traffic }: { traffic: TrafficStats }) {
  const perDay = traffic.perDay.map((d) => ({ label: dayLabel(d.date), value: d.pageViews }));
  const errorTotal = traffic.errors.clientErrors + traffic.errors.serverErrors;
  const errorRate =
    traffic.errors.totalRequests === 0
      ? '0%'
      : `${Math.round((errorTotal / traffic.errors.totalRequests) * 100)}%`;

  return (
    <section className="overview__section" aria-labelledby="overview-traffic">
      <h2 className="overview__heading" id="overview-traffic">
        Site traffic
      </h2>

      <StaleLogsWarning traffic={traffic} />

      <div className="overview__tiles">
        <StatTile label="Page views" value={String(traffic.totals.pageViews)} />
        <StatTile
          label="Unique visitors"
          value={String(traffic.totals.uniques)}
          hint="approx (distinct IPs)"
        />
        <StatTile label="Error rate" value={errorRate} hint="4xx and 5xx" />
      </div>

      <ColumnChart
        data={perDay}
        caption={`Page views per day (last ${traffic.windowDays} days)`}
      />

      {traffic.topPages.length > 0 && (
        <BarChart
          data={traffic.topPages.map((p) => ({ label: p.key, value: p.count }))}
          caption="Top pages"
        />
      )}

      {traffic.topReferrers.length > 0 && (
        <BarChart
          data={traffic.topReferrers.map((r) => ({ label: r.key, value: r.count }))}
          caption="Top referrers"
        />
      )}

      {/*
        byRef is optional on the wire: a summary written before reference links
        existed simply has no field, and it only gains one after the next daily
        run. No data means no chart, never zeros.
      */}
      {(traffic.byRef?.length ?? 0) > 0 && (
        <BarChart
          data={traffic.byRef!.map((r) => ({ label: r.key, value: r.count }))}
          caption="Page views by reference link"
        />
      )}

      <p className="overview__caveat">
        Traffic is derived from server-side CloudFront logs, not from any visitor
        tracker. Geography is not reported: standard logs record the edge
        location, not the visitor. Uniques are approximate.
      </p>
    </section>
  );
}

export function AdminOverview({
  token,
  onAuthError,
}: {
  token: string;
  onAuthError: (message: string) => void;
}) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [traffic, setTraffic] = useState<TrafficStats | null>(null);
  const [trafficChecked, setTrafficChecked] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function onExport() {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await exportSigners(token);
      // Browser download: object URL, synthetic anchor click, then revoke so the
      // blob is not held in memory. jsdom has no real download, but createElement
      // + click are exercised by the test.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'deletedoordash-signers.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 401) {
        onAuthError('That password was rejected.');
        return;
      }
      setExportError('Could not export the signers. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchStats(token)
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onAuthError('That password was rejected.');
          return;
        }
        setStats(null);
        setError('Could not load the analytics.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, onAuthError]);

  useEffect(() => {
    let cancelled = false;
    // Never throws: fetchTraffic resolves to null when the file is absent or not
    // the expected shape, so the section degrades to "not available yet".
    fetchTraffic().then((t) => {
      if (cancelled) return;
      setTraffic(t);
      setTrafficChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="overview" id="admin-overview">
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      {loading && !stats && <p className="notice">Loading analytics...</p>}

      <section className="overview__section" aria-labelledby="overview-export">
        <h2 className="overview__heading" id="overview-export">
          Export signers
        </h2>
        {exportError && (
          <p className="notice notice--error" role="alert">
            {exportError}
          </p>
        )}
        <button
          type="button"
          className="btn btn--primary"
          onClick={onExport}
          disabled={exporting}
        >
          {exporting ? 'Preparing...' : 'Export signers (CSV)'}
        </button>
        <p className="overview__caveat">
          Downloads every signer, including reference-link codes. The file
          contains contact emails, so handle it carefully: it is the same
          sensitive data the moderation queue keeps behind a disclosure.
        </p>
      </section>

      {stats && <StatsSection stats={stats} />}

      {trafficChecked &&
        (traffic ? (
          <TrafficSection traffic={traffic} />
        ) : (
          <section className="overview__section" aria-labelledby="overview-traffic">
            <h2 className="overview__heading" id="overview-traffic">
              Site traffic
            </h2>
            <p className="notice">
              Traffic data is not available yet. It appears once the CloudFront
              logging pipeline has been brought up and its first daily summary has
              run.
            </p>
          </section>
        ))}

      {/* Always shown: the admin needs the link format even before any ref has
          been used, which is exactly when both ref charts are hidden. */}
      <ReferenceLinkNote />
    </div>
  );
}
